/**
 * Cross-Tab Leader Election
 *
 * Ensures only ONE browser tab holds a Socket.IO connection at a time.
 * Uses BroadcastChannel for instant messaging + localStorage heartbeat
 * as a fallback when BroadcastChannel isn't available or for crash detection.
 *
 * Flow:
 * 1. Tab announces candidacy with a heartbeat timestamp
 * 2. Tab with the most recent heartbeat AND open connection wins
 * 3. Leader broadcasts socket events to follower tabs via BroadcastChannel
 * 4. When leader closes, followers race for leadership
 */

const CHANNEL_NAME = 'gabhan-tab-leader';
const HEARTBEAT_KEY = 'gabhan_leader_heartbeat';
const HEARTBEAT_INTERVAL_MS = 4_000;       // leader heartbeat frequency
const LEADERSHIP_TIMEOUT_MS = 10_000;      // if no heartbeat for this long, leader is dead
const ELECTION_DEBOUNCE_MS = 200;          // debounce competing elections

let _tabId;
let _isLeader = false;
let _channel = null;
let _heartbeatTimer = null;
let _electionTimer = null;
let _onBecomeLeader = null;
let _onLoseLeadership = null;
let _onMessage = null;

/** Unique ID for this tab (survives within session) */
const getTabId = () => {
  if (!_tabId) {
    _tabId = `tab_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }
  return _tabId;
};

/** Write leader heartbeat to localStorage */
const writeHeartbeat = () => {
  try {
    localStorage.setItem(HEARTBEAT_KEY, JSON.stringify({
      tabId: getTabId(),
      timestamp: Date.now(),
    }));
  } catch { /* quota exceeded — ignore */ }
};

/** Read current leader from localStorage heartbeat */
const readHeartbeat = () => {
  try {
    const raw = localStorage.getItem(HEARTBEAT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/** Check if the current leader is still alive */
const _isLeaderAlive = () => {
  const hb = readHeartbeat();
  if (!hb) return false;
  if (hb.tabId === getTabId()) return true; // we are the leader
  return (Date.now() - hb.timestamp) < LEADERSHIP_TIMEOUT_MS;
};

/** Attempt to claim leadership */
const tryClaimLeadership = () => {
  clearTimeout(_electionTimer);
  _electionTimer = setTimeout(() => {
    const hb = readHeartbeat();

    // No leader or leader timed out — claim it
    if (!hb || (Date.now() - hb.timestamp) >= LEADERSHIP_TIMEOUT_MS) {
      _becomeLeader();
      return;
    }

    // We are already the leader
    if (hb.tabId === getTabId()) {
      if (!_isLeader) _becomeLeader();
      return;
    }

    // Another tab is the healthy leader — stay follower
    if (_isLeader) {
      _isLeader = false;
      clearInterval(_heartbeatTimer);
      _onLoseLeadership?.();
    }
  }, ELECTION_DEBOUNCE_MS + Math.random() * 100); // stagger to prevent split-brain
};

/** Become the leader */
const _becomeLeader = () => {
  _isLeader = true;
  writeHeartbeat();

  // Start heartbeat
  clearInterval(_heartbeatTimer);
  _heartbeatTimer = setInterval(writeHeartbeat, HEARTBEAT_INTERVAL_MS);

  _onBecomeLeader?.();
};

/**
 * Initialize leader election
 * @param {{ onBecomeLeader: () => void, onLoseLeadership: () => void, onMessage: (msg: any) => void }} callbacks
 */
export function initLeaderElection({ onBecomeLeader, onLoseLeadership, onMessage }) {
  _onBecomeLeader = onBecomeLeader;
  _onLoseLeadership = onLoseLeadership;
  _onMessage = onMessage;

  // Set up BroadcastChannel for cross-tab messaging
  if (typeof BroadcastChannel !== 'undefined') {
    try {
      _channel = new BroadcastChannel(CHANNEL_NAME);
      _channel.onmessage = (event) => {
        const msg = event.data;
        if (msg.type === 'leader-claim' && msg.tabId !== getTabId()) {
          // Another tab claimed leadership
          if (_isLeader) {
            // Yield if their timestamp is newer (last-writer-wins)
            if (msg.timestamp > Date.now() - 500) {
              _isLeader = false;
              clearInterval(_heartbeatTimer);
              _onLoseLeadership?.();
            }
          }
        } else if (msg.type === 'leader-resign') {
          // Leader resigned — start election
          tryClaimLeadership();
        } else if (msg.type === 'socket-event' && !_isLeader) {
          // Follower receives proxied socket event from leader
          _onMessage?.(msg.payload);
        }
      };
    } catch {
      // BroadcastChannel not available — localStorage fallback only
    }
  }

  // Listen for storage events (cross-tab heartbeat changes)
  window.addEventListener('storage', (e) => {
    if (e.key === HEARTBEAT_KEY) {
      const hb = readHeartbeat();
      if (hb && hb.tabId !== getTabId() && _isLeader) {
        // Another tab overwrote our heartbeat — yield
        _isLeader = false;
        clearInterval(_heartbeatTimer);
        _onLoseLeadership?.();
      } else if (!hb || (Date.now() - (hb?.timestamp || 0)) >= LEADERSHIP_TIMEOUT_MS) {
        tryClaimLeadership();
      }
    }
  });

  // Resign on tab close
  window.addEventListener('beforeunload', () => {
    if (_isLeader) {
      try {
        localStorage.removeItem(HEARTBEAT_KEY);
        _channel?.postMessage({ type: 'leader-resign', tabId: getTabId() });
      } catch { /* best effort */ }
    }
  });

  // Initial election
  tryClaimLeadership();
}

/**
 * Broadcast a socket event to follower tabs (call from leader only)
 * @param {{ event: string, data: any }} payload
 */
export function broadcastSocketEvent(event, data) {
  if (!_isLeader || !_channel) return;
  try {
    _channel.postMessage({
      type: 'socket-event',
      tabId: getTabId(),
      payload: { event, data },
    });
  } catch { /* channel closed or message too large */ }
}

/** Check if this tab is the leader */
export function isLeaderTab() {
  return _isLeader;
}

/** Tear down leader election (for testing / HMR) */
export function destroyLeaderElection() {
  _isLeader = false;
  clearInterval(_heartbeatTimer);
  clearTimeout(_electionTimer);
  _channel?.close();
  _channel = null;
  _onBecomeLeader = null;
  _onLoseLeadership = null;
  _onMessage = null;
}
